/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor } from 'lexical';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $isTableNode } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  DEPRECATED_$isGridSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_MODIFIER_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import useModal from './useModal';
import DropDown, { DropDownItem } from './DropDown';
import DropdownColorPicker from './DropdownColorPicker';
import { getSelectedNode } from './getSelectedNode';
import { sanitizeUrl } from './url';
import {
  InsertImageDialog,
} from './Images';
import { InsertTableDialog } from './Table';
import { useTranslation } from 'react-i18next';
import { $createTextNode } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
};

const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

const FONT_FAMILY_OPTIONS: [string, string][] = [
  ['Arial', 'Arial'],
  ['Courier New', 'Courier New'],
  ['Georgia', 'Georgia'],
  ['Times New Roman', 'Times New Roman'],
  ['Trebuchet MS', 'Trebuchet MS'],
  ['Verdana', 'Verdana'],
];

const FONT_SIZE_OPTIONS: [string, string][] = [
  ['10px', '10px'],
  ['11px', '11px'],
  ['12px', '12px'],
  ['13px', '13px'],
  ['14px', '14px'],
  ['15px', '15px'],
  ['16px', '16px'],
  ['17px', '17px'],
  ['18px', '18px'],
  ['19px', '19px'],
  ['20px', '20px'],
];

function dropDownActiveClass(active: boolean) {
  if (active) return 'active dropdown-item-active';
  else return '';
}

function BlockFormatDropDown({
  editor,
  blockType,
  rootType,
  disabled = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  rootType: keyof typeof rootTypeToRootName;
  editor: LexicalEditor;
  disabled?: boolean;
}): JSX.Element {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (
        $isRangeSelection(selection) ||
        DEPRECATED_$isGridSelection(selection)
      ) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if (
          $isRangeSelection(selection) ||
          DEPRECATED_$isGridSelection(selection)
        ) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if (
          $isRangeSelection(selection) ||
          DEPRECATED_$isGridSelection(selection)
        ) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={'icon block-type ' + blockType}
      buttonLabel={blockTypeToBlockName[blockType]}
      buttonAriaLabel="Formatting options for text style">
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'paragraph')}
        onClick={formatParagraph}>
        <i className="icon paragraph" />
        <span className="text">Normal</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h1')}
        onClick={() => formatHeading('h1')}>
        <i className="icon h1" />
        <span className="text">Heading 1</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h2')}
        onClick={() => formatHeading('h2')}>
        <i className="icon h2" />
        <span className="text">Heading 2</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h3')}
        onClick={() => formatHeading('h3')}>
        <i className="icon h3" />
        <span className="text">Heading 3</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'bullet')}
        onClick={formatBulletList}>
        <i className="icon bullet-list" />
        <span className="text">Bullet List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'number')}
        onClick={formatNumberedList}>
        <i className="icon numbered-list" />
        <span className="text">Numbered List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'check')}
        onClick={formatCheckList}>
        <i className="icon check-list" />
        <span className="text">Check List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'quote')}
        onClick={formatQuote}>
        <i className="icon quote" />
        <span className="text">Quote</span>
      </DropDownItem>
    </DropDown>
  );
}

function Divider(): JSX.Element {
  return <div className="divider" />;
}

function FontDropDown({
  editor,
  value,
  style,
  disabled = false,
}: {
  editor: LexicalEditor;
  value: string;
  style: string;
  disabled?: boolean;
}): JSX.Element {
  const handleClick = useCallback(
    (option: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            [style]: option,
          });
        }
      });
    },
    [editor, style],
  );

  const buttonAriaLabel =
    style === 'font-family'
      ? 'Formatting options for font family'
      : 'Formatting options for font size';

  return (
    <DropDown
      disabled={disabled}
      buttonClassName={'toolbar-item ' + style}
      buttonLabel={value}
      buttonIconClassName={
        style === 'font-family' ? 'icon block-type font-family' : ''
      }
      buttonAriaLabel={buttonAriaLabel}>
      {(style === 'font-family' ? FONT_FAMILY_OPTIONS : FONT_SIZE_OPTIONS).map(
        ([option, text]) => (
          <DropDownItem
            className={`item ${dropDownActiveClass(value === option)} ${style === 'font-size' ? 'fontsize-item' : ''
              }`}
            onClick={() => handleClick(option)}
            key={option}>
            <span className="text">{text}</span>
          </DropDownItem>
        ),
      )}
    </DropDown>
  );
}

export default function RTEToolbar(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [rootType, setRootType] =
    useState<keyof typeof rootTypeToRootName>('root');
  const [fontSize, setFontSize] = useState<string>('15px');
  const [fontColor, setFontColor] = useState<string>('#000');
  const [bgColor, setBgColor] = useState<string>('#fff');
  const [fontFamily, setFontFamily] = useState<string>('Arial');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [modal, showModal] = useModal();
  const [isRTL, setIsRTL] = useState(false);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const { t } = useTranslation();

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsCode(selection.hasFormat('code'));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType('table');
      } else {
        setRootType('root');
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }
      // Handle buttons
      setFontSize(
        $getSelectionStyleValueForProperty(selection, 'font-size', '15px'),
      );
      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
      setFontFamily(
        $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'),
      );
    }
  }, [activeEditor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      })
    );
  }, [$updateToolbar, activeEditor, editor]);

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event: KeyboardEvent = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          return activeEditor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            sanitizeUrl('https://'),
          );
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [activeEditor, isLink]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [activeEditor],
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value });
    },
    [applyStyleText],
  );

  const onBgColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ 'background-color': value });
    },
    [applyStyleText],
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const insertLinkPlaceholder = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const paragraphNode = $createParagraphNode(); // Create a paragraph node
        const textNode = $createTextNode('[INSERT LINK HERE]'); // Create the text node
        paragraphNode.append(textNode); // Append the text node to the paragraph
        selection.insertNodes([paragraphNode]); // Insert the paragraph into the selection
      }
    });
  };

  const insertSupplierPlaceholder = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const paragraphNode = $createParagraphNode(); // Create a paragraph node
        const textNode = $createTextNode('[SUPPLIER_NAME]'); // Create the text node
        paragraphNode.append(textNode); // Append the text node to the paragraph
        selection.insertNodes([paragraphNode]); // Insert the paragraph into the selection
      }
    });
  };

  const insertSignatureFooter = () => {
    const signatureHtml = `
      <div class="email-signature">
        <p>Med Vänliga Hälsningar</p>
        <p><b>John Doe</b></p>
        <p>Company Role</p>
        <p>+123-456-7890</p>
        <p>john.doe@example.com</p>
        <p> </p>
        <p>
        <img src="data:image/png;base64,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" alt="Company Logo" style="width:60px;height:60px; margin-top: 10px;" />
        </p>
        <p> </p>
        <p><b><i>Join the work for a more sustainable society. Avoid printing your mail.</i></b></p>
        <p style="font-size: 12px;">
          Detta mail kan innehålla konfidentiell information. Spridning, vidaresändning eller kopiering är inte tillåten. Det är mottagarens ansvar att informationen enbart når de som är i direkt behov av informationen för fullgörande av sina arbetsuppgifter. Vid misstanke eller kännedom om spridning av konfidentiell information till tredje man ber vi er underrätta oss. 
          This e-mail may contain confidential information. Any dissemination, distribution or copying is not allowed. It is the responsibility of the receiver to ensure this information only reaches persons who need it for the fulfillment of their duties. At suspicion or knowledge of dissemination, please let us know.
        </p>
      </div>
    `;

    editor.update(() => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(signatureHtml, 'text/html');
      const nodes = $generateNodesFromDOM(editor, doc);

      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertNodes(nodes); // Insert decoded HTML nodes
      }
    });
  };

  return (
    <div className="toolbar">
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <>
          <BlockFormatDropDown
            disabled={!isEditable}
            blockType={blockType}
            rootType={rootType}
            editor={editor}
          />
          <Divider />
        </>
      )}

      <>
        <FontDropDown
          disabled={!isEditable}
          style={'font-family'}
          value={fontFamily}
          editor={editor}
        />
        <FontDropDown
          disabled={!isEditable}
          style={'font-size'}
          value={fontSize}
          editor={editor}
        />
        <Divider />
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
          className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
          title={'Bold (Ctrl+B)'}
          type="button"
          aria-label={`Format text as bold. Shortcut: ${'Ctrl+B'
            }`}>
          <i className="format bold" />
        </button>
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
          title={'Italic (Ctrl+I)'}
          type="button"
          aria-label={`Format text as italics. Shortcut: ${'Ctrl+I'
            }`}>
          <i className="format italic" />
        </button>
        <button
          disabled={!isEditable}
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
          title={'Underline (Ctrl+U)'}
          type="button"
          aria-label={`Format text to underlined. Shortcut: ${'Ctrl+U'
            }`}>
          <i className="format underline" />
        </button>
        <button
          disabled={!isEditable}
          onClick={insertLink}
          className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
          aria-label="Insert link"
          title="Insert link"
          type="button">
          <i className="format link" />
        </button>
        <DropdownColorPicker
          disabled={!isEditable}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting text color"
          buttonIconClassName="icon font-color"
          color={fontColor}
          onChange={onFontColorSelect}
          title="text color"
        />
        <DropdownColorPicker
          disabled={!isEditable}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting background color"
          buttonIconClassName="icon bg-color"
          color={bgColor}
          onChange={onBgColorSelect}
          title="bg color"
        />
        <Divider />
        <DropDown
          disabled={!isEditable}
          buttonClassName="toolbar-item spaced"
          buttonLabel="Insert"
          buttonAriaLabel="Insert specialized editor node"
          buttonIconClassName="icon plus">
          <DropDownItem
            onClick={() => {
              activeEditor.dispatchCommand(
                INSERT_HORIZONTAL_RULE_COMMAND,
                undefined,
              );
            }}
            className="item">
            <i className="icon horizontal-rule" />
            <span className="text">Horizontal Rule</span>
          </DropDownItem>
          <DropDownItem
            onClick={() => {
              showModal(t('RTE.image.dialogTitle').toString(), (onClose) => (
                <InsertImageDialog
                  activeEditor={activeEditor}
                  onClose={onClose}
                />
              ));
            }}
            className="item">
            <i className="icon image" />
            <span className="text">Image</span>
          </DropDownItem>
          <DropDownItem
            onClick={() => {
              showModal(t('RTE.table.dialogTitle').toString(), (onClose) => (
                <InsertTableDialog
                  activeEditor={activeEditor}
                  onClose={onClose}
                />
              ));
            }}
            className="item">
            <i className="icon RTEtable" />
            <span className="text">Table</span>
          </DropDownItem>
        </DropDown>
      </>

      <Divider />
      <DropDown
        disabled={!isEditable}
        buttonLabel="Align"
        buttonIconClassName="icon left-align"
        buttonClassName="toolbar-item spaced alignment"
        buttonAriaLabel="Formatting options for text alignment">
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
          }}
          className="item">
          <i className="icon left-align" />
          <span className="text">Left Align</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
          }}
          className="item">
          <i className="icon center-align" />
          <span className="text">Center Align</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
          }}
          className="item">
          <i className="icon right-align" />
          <span className="text">Right Align</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
          }}
          className="item">
          <i className="icon justify-align" />
          <span className="text">Justify Align</span>
        </DropDownItem>
        <Divider />
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          }}
          className="item">
          <i className={'icon ' + (isRTL ? 'indent' : 'outdent')} />
          <span className="text">Outdent</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }}
          className="item">
          <i className={'icon ' + (isRTL ? 'outdent' : 'indent')} />
          <span className="text">Indent</span>
        </DropDownItem>
      </DropDown>
      <DropDown
        disabled={!isEditable}
        buttonClassName="toolbar-item spaced"
        buttonLabel="Placeholders"
        buttonAriaLabel="Insert specialized editor content"
        buttonIconClassName="icon plus"
      >
        <DropDownItem
          onClick={insertLinkPlaceholder}
          className="item"
        >
          <i className="icon link" />
          <span className="text">Insert Link Placeholder</span>
        </DropDownItem>
        <DropDownItem
          onClick={insertSupplierPlaceholder}
          className="item"
        >
          <i className="icon supplier" />
          <span className="text">Insert Supplier Name</span>
        </DropDownItem>
      </DropDown>
      <button onClick={insertSignatureFooter} className="toolbar-item spaced">
        Insert Signature
      </button>

      {modal}
    </div>
  );
}